// The raw submissions, per student — visible BEFORE any assessment (the
// requirement the prototype demo did not meet: there, a student's work could
// only be opened after it had been graded). Lists the files each student
// handed in, with real thumbnails, and opens the same SubmissionViewer the
// graded views use. Nothing here depends on grades; after assessment the
// panel simply gains a "View progress" link per student.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import AssetThumb from '../components/features/AssetThumb';
import SubmissionViewer from '../components/overview/SubmissionViewer';
import { Badge, Button } from '../components/ds';
import { projectFixtures } from '../data/fixtures';
import { useProjectDashboard } from '../data/hooks';
import { demoStudentPath } from '../data/projects';

function initialsOf(name) {
  return (name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const KIND_LABEL = { image: 'photo', document: 'document', video: 'video' };

export default function StudentWorkPanel({ adProjectId, cardId }) {
  const { data } = useProjectDashboard(adProjectId);
  const fixture = projectFixtures[adProjectId];
  const [viewerStudent, setViewerStudent] = useState(null);
  if (!data || !fixture) return null;

  const anyGraded = data.students.some((s) => s.skills_graded > 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-border bg-card px-6 py-5 shadow-card">
        <h2 className="text-[16px] font-semibold text-foreground">Submitted work</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          {anyGraded
            ? 'The files each student handed in, exactly as submitted. Open the work to read it, or view a student’s progress to see how it was assessed.'
            : 'The files each student handed in, exactly as submitted — photos and documents in their original form. Nothing has been assessed yet.'}
        </p>
      </div>

      {data.students.map((student) => {
        const subs = fixture.submissions[String(student.student_id)] ?? [];
        const files = subs.flatMap((s) => s.assets.filter((a) => a.parent_asset_id == null));
        const submittedAt = subs.map((s) => s.created_at).sort().pop();
        const assessed = student.skills_graded > 0;
        return (
          <div key={student.student_id} className="rounded-2xl border border-border bg-card px-6 py-5 shadow-card">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-[13px] font-bold text-primary-foreground">
                {initialsOf(student.student_name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold text-foreground">{student.student_name}</div>
                <div className="text-[12.5px] text-muted-foreground">
                  {files.length} file{files.length === 1 ? '' : 's'}
                  {submittedAt ? ` · submitted ${fmtDate(submittedAt)}` : ''}
                </div>
              </div>
              {assessed ? (
                <Badge tone="primary">{student.skills_graded} descriptors assessed</Badge>
              ) : (
                <Badge tone="outline">Not yet assessed</Badge>
              )}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setViewerStudent(student)}>
                  Open work
                </Button>
                {assessed && (
                  <Link
                    to={demoStudentPath(cardId, student.student_id)}
                    className="inline-flex h-8 items-center rounded-full bg-primary px-3.5 text-xs font-semibold text-primary-foreground"
                  >
                    View progress →
                  </Link>
                )}
              </div>
            </div>

            {files.length === 0 ? (
              <p className="mt-4 text-[13px] italic text-muted-foreground">No files registered for this student.</p>
            ) : (
              <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {files.map((asset) => (
                  <li key={asset.id}>
                    <button
                      type="button"
                      onClick={() => setViewerStudent(student)}
                      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2 text-left transition-colors hover:border-primary/50"
                      title="Open in the work viewer"
                    >
                      <AssetThumb asset={asset} className="h-14 w-20 shrink-0" />
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px] font-semibold text-foreground">
                          {asset.original_name || `${asset.kind} ${asset.id}`}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">
                          {KIND_LABEL[asset.kind] ?? asset.kind}
                          {asset.size_bytes ? ` · ${Math.max(1, Math.round(asset.size_bytes / 1024))} KB` : ''}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {/* raw = files only until this student has been assessed; afterwards the
          viewer shows what the AI read (transcripts, regions, extracted pages). */}
      <SubmissionViewer
        projectId={adProjectId}
        student={viewerStudent}
        raw={!viewerStudent || (data.students.find((s) => s.student_id === viewerStudent.student_id)?.skills_graded ?? 0) === 0}
        onClose={() => setViewerStudent(null)}
      />
    </div>
  );
}
